import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ThemeProvider } from "next-themes";
import { lazy, Suspense, useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { usePwaUpdate } from "@/hooks/usePwaUpdate";
import { ConsentScreen, useGdprConsent } from "@/components/GDPR/ConsentScreen";
import { CookieBanner } from "@/components/GDPR/CookieBanner";

const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Settings = lazy(() => import("./pages/Settings"));
const Privacy = lazy(() => import("./pages/Privacy"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

function ConsentGate({ children }: { children: React.ReactNode }) {
  const { hasConsent } = useGdprConsent();
  const [accepted, setAccepted] = useState(hasConsent);
  const location = useLocation();

  // Allow /privacy and /auth without consent
  const bypassRoutes = ["/privacy", "/auth"];
  if (bypassRoutes.some(r => location.pathname.startsWith(r))) {
    return <>{children}</>;
  }

  if (!accepted) {
    return <ConsentScreen onAccept={() => setAccepted(true)} />;
  }

  return <>{children}</>;
}

function AnimatedRoutes() {
  const location = useLocation();
  
  // Determine transition direction based on route
  const getVariants = (path: string) => {
    if (path === "/settings" || path === "/dashboard") {
      return {
        initial: { y: "100%", opacity: 0 },
        animate: { y: 0, opacity: 1 },
        exit: { y: "100%", opacity: 0 },
      };
    }
    return {
      initial: { opacity: 0, scale: 0.98 },
      animate: { opacity: 1, scale: 1 },
      exit: { opacity: 0, scale: 0.98 },
    };
  };
  
  const variants = getVariants(location.pathname);

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={variants.initial}
        animate={variants.animate}
        exit={variants.exit}
        transition={{
          type: "spring",
          damping: 25,
          stiffness: 200,
          mass: 0.8,
        }}
        className="min-h-screen"
      >
        <Suspense fallback={<div className="min-h-screen bg-background" />}>
          <Routes location={location}>
            <Route path="/" element={<Index />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
}

function AppWithUpdater({ children }: { children: React.ReactNode }) {
  usePwaUpdate();

  // Prevent unhandled promise rejections from crashing the app
  useEffect(() => {
    const handleRejection = (event: PromiseRejectionEvent) => {
      console.error("Unhandled rejection:", event.reason);
      event.preventDefault();
    };
    window.addEventListener("unhandledrejection", handleRejection);
    return () => window.removeEventListener("unhandledrejection", handleRejection);
  }, []);

  return <>{children}</>;
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
        <AuthProvider>
          <TooltipProvider delayDuration={300}>
            <AppWithUpdater>
              <Sonner />
              <BrowserRouter>
                <ConsentGate>
                  <AnimatedRoutes />
                </ConsentGate>
                <CookieBanner />
              </BrowserRouter>
            </AppWithUpdater>
          </TooltipProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
